import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl } from '@angular/forms';
import { ExtensionsService, PreviewFlagResponse } from '@pages/checkout-app/services/extensions/extensions.service';
import { EMPTY, Observable, timer } from 'rxjs';
import { catchError, distinctUntilChanged, expand, filter, startWith, switchMap } from 'rxjs/operators';

const REFRESH_BEFORE_EXPIRY_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 30_000;

/**
 * Read-only input with a short-lived token that bypasses the extension's rules when pasted into checkout address line 2
 */
@Component({
  selector: 'pdq-extension-preview-token',
  templateUrl: './extension-preview-token.component.html',
  standalone: false,
})
export class ExtensionPreviewTokenComponent implements OnInit {
  @Input({ required: true }) usageTokenControl: AbstractControl<string>;

  readonly previewTokenControl = new FormControl<string>({ value: '', disabled: true });

  private readonly destroyRef = inject(DestroyRef);
  private readonly extensionsService = inject(ExtensionsService);

  ngOnInit(): void {
    this.usageTokenControl.valueChanges
      .pipe(
        startWith(this.usageTokenControl.value),
        filter(Boolean),
        distinctUntilChanged(),
        switchMap((usageToken) => this.keepPreviewFlagFresh(usageToken)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ previewFlag }) => this.previewTokenControl.setValue(previewFlag));
  }

  private keepPreviewFlagFresh(usageToken: string): Observable<PreviewFlagResponse> {
    const createPreviewFlag = () => this.extensionsService.createPreviewFlag(usageToken);
    return createPreviewFlag().pipe(
      expand(({ expiresAt }) => timer(this.refreshDelayMs(expiresAt)).pipe(switchMap(createPreviewFlag))),
      catchError(() => EMPTY),
    );
  }

  private refreshDelayMs(expiresAt: string): number {
    return Math.max(new Date(expiresAt).getTime() - Date.now() - REFRESH_BEFORE_EXPIRY_MS, MIN_REFRESH_DELAY_MS);
  }
}
